<template>
  <div class="relative flex flex-col min-w-0 break-words bg-white w-full mb-6 border border-blueGray-200 rounded-lg">

    <!-- Header -->
    <div class="rounded-t mb-0 px-4 py-3 border-0">
      <div class="flex flex-wrap items-center justify-between gap-3">
        <div class="flex items-center gap-3">
          <h3 class="font-semibold text-base text-blueGray-700">Orders</h3>
          <span class="text-xs text-blueGray-400">
            {{ (ecomOrders().data || []).length }} order{{ (ecomOrders().data || []).length !== 1 ? 's' : '' }}
          </span>
        </div>
        <button
          type="button"
          class="flex items-center bg-blueGray-900 hover:bg-blueGray-800 text-white font-bold uppercase text-xs px-4 py-2 rounded-lg shadow hover:shadow-md focus:outline-none transition-all"
          @click="openCreate"
        >
          <i class="fa fa-plus" style="margin-right: 8px;"></i>
          New Order
        </button>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loaders().getEcomOrders" class="flex items-center justify-center py-12 px-4">
      <span class="loader block"></span>
    </div>

    <!-- Empty -->
    <div v-else-if="!(ecomOrders().data || []).length" class="flex flex-col items-center justify-center py-12 px-4 text-blueGray-300">
      <i class="pi pi-receipt text-4xl mb-3"></i>
      <p class="text-sm">No orders yet.</p>
    </div>

    <!-- Orders grid -->
    <div v-else class="px-4 pb-4 flex flex-wrap -mx-2">
      <div
        v-for="(order, i) in (ecomOrders().data || [])"
        :key="order.id || i"
        class="w-full md:w-6/12 px-2 mb-4"
      >
        <div class="rounded-lg border border-blueGray-200 overflow-hidden h-full">

          <!-- Order header row -->
          <div class="flex items-center justify-between px-4 py-3 bg-blueGray-50">
            <div class="cursor-pointer flex-1" @click="toggleExpand(order)">
              <p class="text-xs font-bold text-blueGray-700 font-mono tracking-wide">{{ order.code }}</p>
              <p class="text-xs text-blueGray-400 mt-0.5">
                {{ totalItems(order) }} item{{ totalItems(order) !== 1 ? 's' : '' }}
                &nbsp;·&nbsp;
                {{ formatDate(order.created_at) }}
              </p>
            </div>
            <div class="flex items-center gap-2 flex-shrink-0 ml-3">
              <span
                class="text-xs font-bold px-2 py-1 rounded-full uppercase"
                :class="statusClass(order.status)"
              >{{ order.status }}</span>
              <button
                @click="showInfo(order)"
                class="text-blueGray-400 hover:text-emerald-600 transition-colors"
                title="View details"
              >
                <i class="fa fa-eye text-xs"></i>
              </button>
              <i
                class="fa text-xs text-blueGray-400 cursor-pointer"
                :class="expanded === order.id ? 'fa-chevron-up' : 'fa-chevron-down'"
                @click="toggleExpand(order)"
              ></i>
            </div>
          </div>

          <!-- Expanded item list -->
          <div v-if="expanded === order.id">
            <template v-for="(cart, ci) in order.carts">
              <div
                v-for="(item, ii) in cart.items"
                :key="`${ci}-${ii}`"
                class="flex items-start gap-4 px-4 py-4 border-t border-blueGray-100"
              >
                <div class="w-10 h-10 rounded-md overflow-hidden bg-blueGray-100 flex-shrink-0 border border-blueGray-200">
                  <img
                    v-if="item.thumbnails && item.thumbnails.length"
                    :src="item.thumbnails[0]"
                    class="w-full h-full object-cover"
                    :alt="item.name"
                  />
                  <div v-else class="w-full h-full flex items-center justify-center text-blueGray-300">
                    <i class="fa fa-image text-lg"></i>
                  </div>
                </div>

                <div class="flex-1 min-w-0">
                  <div class="flex items-start justify-between gap-2">
                    <div class="min-w-0">
                      <p class="text-sm font-semibold text-blueGray-700 leading-tight">{{ item.name }}</p>
                      <p v-if="item.sub_title" class="text-xs text-blueGray-400 truncate mt-0.5">{{ item.sub_title }}</p>
                    </div>
                    <div class="text-right flex-shrink-0">
                      <p class="text-sm font-bold text-blueGray-700">
                        {{ formatter("NGN").format(item.pivot.price * item.pivot.quantity) }}
                      </p>
                      <p class="text-xs text-blueGray-400">× {{ item.pivot.quantity }}</p>
                    </div>
                  </div>

                  <div v-if="item.pivot && item.pivot.plan_user" class="mt-2">
                    <span class="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 text-xs font-semibold px-2 py-1 rounded-full border border-emerald-200">
                      <i class="pi pi-barcode text-xs"></i>
                      {{ item.pivot.plan_user.name }}
                    </span>
                  </div>

                  <div
                    v-if="item.pivot && item.pivot.plan_user && item.pivot.plan_user.loans && item.pivot.plan_user.loans.length"
                    class="mt-2 flex flex-wrap gap-1"
                  >
                    <span
                      v-for="(loan, li) in item.pivot.plan_user.loans"
                      :key="li"
                      class="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full border font-medium"
                      :class="loanPillClass(loan)"
                    >
                      {{ formatter("NGN").format(loan.amount) }}
                      <span class="opacity-70 font-normal">{{ shortDate(loan.due_at) }}</span>
                    </span>
                  </div>
                </div>
              </div>
            </template>

            <div class="flex items-center justify-between px-4 py-3 bg-blueGray-50 border-t border-blueGray-200">
              <span class="text-xs font-semibold text-blueGray-500 uppercase tracking-wide">Order Total</span>
              <span class="text-sm font-bold text-blueGray-700">
                {{ formatter("NGN").format(orderTotal(order)) }}
              </span>
            </div>

            <div class="px-4 py-3 border-t border-blueGray-100">
              <label class="block uppercase text-blueGray-500 text-xs font-bold mb-2">Status</label>
              <select
                :value="order.status"
                @change="onStatusChange(order, $event.target.value)"
                :disabled="loaders().updateOrderStatus"
                class="border-0 px-3 py-2 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none w-full"
              >
                <option v-for="s in orderStatuses" :key="s" :value="s">{{ s }}</option>
              </select>
            </div>

            <div class="px-4 py-3 border-t border-blueGray-100">
              <div class="flex items-center justify-between mb-2">
                <label class="block uppercase text-blueGray-500 text-xs font-bold">Addresses</label>
                <span v-if="loaders().getOrderAddresses" class="text-xs text-blueGray-400">Loading…</span>
              </div>

              <p v-if="!loaders().getOrderAddresses && !orderAddresses.length" class="text-xs text-blueGray-400">
                This customer has no saved addresses yet.
              </p>

              <template v-else>
                <label class="block text-blueGray-500 text-xs font-semibold mb-1">Billing</label>
                <select
                  v-model="addressEdit.billing_address_id"
                  class="border-0 px-3 py-2 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none w-full mb-3"
                >
                  <option value="">None</option>
                  <option v-for="addr in orderAddresses" :key="addr.id" :value="addr.id">
                    {{ addressLabel(addr) }}
                  </option>
                </select>

                <label class="block text-blueGray-500 text-xs font-semibold mb-1">Shipping</label>
                <select
                  v-model="addressEdit.shipping_address_id"
                  class="border-0 px-3 py-2 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none w-full"
                >
                  <option value="">None</option>
                  <option v-for="addr in orderAddresses" :key="addr.id" :value="addr.id">
                    {{ addressLabel(addr) }}
                  </option>
                </select>

                <button
                  type="button"
                  class="mt-3 w-full bg-blueGray-900 hover:bg-blueGray-800 text-white font-bold uppercase text-xs px-4 py-3 rounded-lg shadow focus:outline-none transition-all disabled:opacity-50"
                  :disabled="loaders().updateOrderAddresses"
                  @click="onSaveAddresses(order)"
                >
                  {{ loaders().updateOrderAddresses ? 'Saving…' : 'Save Addresses' }}
                </button>
              </template>
            </div>

            <div class="px-4 py-3 border-t border-blueGray-100">
              <p v-if="order.payment_method === 'CASH'" class="text-xs text-emerald-600">
                <i class="fa fa-check-circle" style="margin-right: 4px;"></i>
                Paid by cash{{ order.paid_at ? ' on ' + formatDate(order.paid_at) : '' }}.
              </p>
              <button
                v-else
                type="button"
                class="w-full bg-blueGray-900 hover:bg-blueGray-800 text-white font-bold uppercase text-xs px-4 py-3 rounded-lg shadow focus:outline-none transition-all disabled:opacity-50"
                :disabled="loaders().markOrderPaid"
                @click="onMarkPaid(order)"
              >
                {{ loaders().markOrderPaid ? 'Marking…' : 'Mark as Paid' }}
              </button>
            </div>
          </div>

        </div>
      </div>
    </div>

    <!-- Info Modal -->
    <transition-group>
      <transition name="bounce" :duration="{ enter: 500, leave: 800 }" :key="'order-modal'">
        <div
          v-if="modals.info && selectedOrder"
          @click.self="$_close_all_modal"
          style="width: 100%;"
          class="overflow-x-hidden overflow-y-auto fixed inset-0 z-40 outline-none focus:outline-none justify-center items-center flex w-screen h-screen top-0 left-0"
        >
          <div class="relative w-auto z-50">
            <div class="border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none">
              <div class="flex items-start justify-between p-5 border-b border-solid border-blueGray-200 rounded-t">
                <h3 class="text-xl font-semibold capitalize">Order Details</h3>
                <button
                  class="p-1 ml-auto bg-transparent border-0 text-black opacity-5 float-right text-3xl leading-none font-semibold outline-none focus:outline-none"
                  @click="toggleModal('info', false)"
                >
                  <span class="bg-transparent text-black opacity-5 h-6 w-6 text-2xl block outline-none focus:outline-none">×</span>
                </button>
              </div>
              <div class="relative p-6 flex-auto" style="max-height: 460px; overflow-y: auto;">
                <recursive-table
                  :data="selectedOrder"
                  :exclude="['id', 'billing_address_id', 'shipping_address_id', 'user_id']"
                  :copies="['code', 'uuid']"
                  :dates="['created_at', 'updated_at']"
                  :emails="['email']"
                  :tels="['phone']"
                />
              </div>
              <div class="flex items-center justify-end p-4 border-t border-solid border-blueGray-200 rounded-b">
                <button
                  class="text-red-500 background-transparent capitalize px-6 py-2 text-sm outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150"
                  type="button"
                  @click="toggleModal('info', false)"
                >Close</button>
              </div>
            </div>
          </div>
        </div>
      </transition>
      <transition name="bounce" :duration="{ enter: 500, leave: 800 }" :key="'order-overlay'">
        <div v-if="modals.info" @click="$_close_all_modal" style="width: 100%; opacity: 0.25;" class="opacity-25 fixed inset-0 z-30 h-screen top-0 left-0 bg-black"></div>
      </transition>
    </transition-group>

    <!-- Create Order Modal -->
    <transition-group>
      <transition name="bounce" :duration="{ enter: 500, leave: 800 }" :key="'create-order-modal'">
        <div
          v-if="modals.create"
          @click.self="$_close_all_modal"
          style="width: 100%;"
          class="overflow-x-hidden overflow-y-auto fixed inset-0 z-40 outline-none focus:outline-none justify-center items-center flex w-screen h-screen top-0 left-0"
        >
          <div class="relative w-auto z-50" style="width: 90%; max-width: 560px;">
            <div class="border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none">
              <div class="flex items-start justify-between p-5 border-b border-solid border-blueGray-200 rounded-t">
                <h3 class="text-xl font-semibold capitalize">New Order</h3>
                <button
                  class="p-1 ml-auto bg-transparent border-0 text-black opacity-5 float-right text-3xl leading-none font-semibold outline-none focus:outline-none"
                  @click="toggleModal('create', false)"
                >
                  <span class="bg-transparent text-black opacity-5 h-6 w-6 text-2xl block outline-none focus:outline-none">×</span>
                </button>
              </div>

              <div class="relative p-6 flex-auto" style="max-height: 60vh; overflow-y: auto;">
                <p class="text-xs text-blueGray-400 mb-4">
                  Places an order on behalf of a customer — same payment plans as a normal
                  checkout, charged to their wallet. If they end up paying cash in person
                  instead, use "Mark as Paid" from the order's details afterwards.
                </p>

                <user-search-select
                  label="Customer"
                  v-model="newOrder.user"
                  class="mb-4"
                />

                <label class="block uppercase text-blueGray-600 text-xs font-bold mb-2">Items</label>
                <div
                  v-for="(line, i) in newOrder.items"
                  :key="i"
                  class="flex items-start gap-2 mb-3"
                >
                  <item-search-select
                    v-model="line.item"
                    class="flex-1"
                  />

                  <input
                    v-model.number="line.quantity"
                    type="number"
                    min="1"
                    placeholder="Qty"
                    class="border-0 px-3 py-2 placeholder-blueGray-300 text-blueGray-600 bg-blueGray-50 rounded text-sm shadow focus:outline-none"
                    style="width: 70px;"
                  />

                  <select
                    v-model="line.plan_id"
                    class="border-0 px-3 py-2 placeholder-blueGray-300 text-blueGray-600 bg-blueGray-50 rounded text-sm shadow focus:outline-none"
                    style="width: 140px;"
                  >
                    <option value="">Pay Once</option>
                    <option v-for="plan in planOptions(line)" :key="plan.id" :value="plan.id">
                      {{ plan.name }}
                    </option>
                  </select>

                  <button
                    v-if="newOrder.items.length > 1"
                    type="button"
                    class="text-blueGray-400 hover:text-red-500 px-2"
                    @click="removeLine(i)"
                  >
                    <i class="fa fa-times"></i>
                  </button>
                </div>

                <button
                  type="button"
                  class="text-emerald-600 text-xs font-semibold mt-1"
                  @click="addLine"
                >
                  <i class="fa fa-plus" style="margin-right: 4px;"></i> Add another item
                </button>

                <p v-if="createError" class="text-red-500 text-xs mt-4">{{ createError }}</p>
              </div>

              <div class="flex items-center justify-end p-4 border-t border-solid border-blueGray-200 rounded-b">
                <button
                  class="text-blueGray-500 background-transparent capitalize px-6 py-2 text-sm outline-none focus:outline-none mr-1 mb-1"
                  type="button"
                  @click="toggleModal('create', false)"
                >Cancel</button>
                <button
                  class="bg-blueGray-900 hover:bg-blueGray-800 text-white font-bold uppercase text-xs px-6 py-3 rounded-lg shadow focus:outline-none transition-all disabled:opacity-50"
                  type="button"
                  :disabled="loaders().createManualOrder"
                  @click="submitCreate"
                >{{ loaders().createManualOrder ? 'Creating…' : 'Create Order' }}</button>
              </div>
            </div>
          </div>
        </div>
      </transition>
      <transition name="bounce" :duration="{ enter: 500, leave: 800 }" :key="'create-order-overlay'">
        <div v-if="modals.create" @click="$_close_all_modal" style="width: 100%; opacity: 0.25;" class="opacity-25 fixed inset-0 z-30 h-screen top-0 left-0 bg-black"></div>
      </transition>
    </transition-group>

  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { formatter } from '../../utils/formatter.util';
import { modal } from '../../mixins';
import RecursiveTable from '@/components/Tables/RecursiveTable.vue';
import UserSearchSelect from '@/components/Form/UserSearchSelect.vue';
import ItemSearchSelect from '@/components/Form/ItemSearchSelect.vue';
import { createToaster } from '@meforma/vue-toaster';

const toaster = createToaster({});

const ORDER_STATUSES = ['PENDING', 'PROCESSING', 'COMPLETED', 'CANCELLED'];

const emptyLine = () => ({ item: null, quantity: 1, plan_id: '' });

export default {
  mixins: [modal],
  components: { RecursiveTable, UserSearchSelect, ItemSearchSelect },
  data() {
    return {
      expanded: null,
      selectedOrder: null,
      modals: { info: false, create: false },
      orderStatuses: ORDER_STATUSES,
      newOrder: { user: null, items: [emptyLine()] },
      createError: '',
      orderAddresses: [],
      addressEdit: { billing_address_id: '', shipping_address_id: '' },
    };
  },
  methods: {
    ...mapGetters(['ecomOrders', 'loaders']),
    ...mapActions([
      'getEcomOrders',
      'createManualOrder',
      'updateOrderStatus',
      'markOrderPaid',
      'getOrderAddresses',
      'updateOrderAddresses',
    ]),

    toggleModal(name, val) { this.modals[name] = val; },

    showInfo(order) {
      this.selectedOrder = order;
      this.toggleModal('info', true);
    },

    addressLabel(addr) {
      return [addr.address, addr.street, addr.lga, addr.state].filter(Boolean).join(', ') || `Address #${addr.id}`;
    },

    onSaveAddresses(order) {
      this.updateOrderAddresses({
        id: order.id,
        billing_address_id:  this.addressEdit.billing_address_id  || null,
        shipping_address_id: this.addressEdit.shipping_address_id || null,
      })
        .catch(err => toaster.error(err?.message || 'Could not update this order\'s addresses.'));
    },

    openCreate() {
      this.newOrder = { user: null, items: [emptyLine()] };
      this.createError = '';
      this.toggleModal('create', true);
    },

    addLine() {
      this.newOrder.items.push(emptyLine());
    },

    removeLine(i) {
      this.newOrder.items.splice(i, 1);
    },

    planOptions(line) {
      return (line.item && line.item.plans) || [];
    },

    submitCreate() {
      this.createError = '';

      if (!this.newOrder.user) {
        this.createError = 'Pick a customer for this order.';
        return;
      }

      const items = this.newOrder.items
        .filter(line => line.item)
        .map(line => ({
          item_id:  line.item.id,
          quantity: line.quantity || 1,
          plan_id:  line.plan_id || null,
        }));

      if (!items.length) {
        this.createError = 'Add at least one item.';
        return;
      }

      this.createManualOrder({
        user_id: this.newOrder.user.id,
        items,
      })
        .then(() => {
          this.toggleModal('create', false);
        })
        .catch(err => {
          this.createError = (err && err.message) || 'Could not create this order.';
        });
    },

    onStatusChange(order, status) {
      this.updateOrderStatus({ id: order.id, status })
        .catch(err => toaster.error(err?.message || 'Could not update this order\'s status.'));
    },

    onMarkPaid(order) {
      this.markOrderPaid(order.id)
        .catch(err => toaster.error(err?.message || 'Could not mark this order as paid.'));
    },

    toggleExpand(order) {
      if (this.expanded === order.id) {
        this.expanded = null;
        return;
      }

      this.expanded = order.id;
      this.orderAddresses = [];
      this.addressEdit = {
        billing_address_id:  order.billing_address_id  || '',
        shipping_address_id: order.shipping_address_id || '',
      };

      this.getOrderAddresses(order.id)
        .then(addresses => { this.orderAddresses = addresses || []; })
        .catch(() => { this.orderAddresses = []; });
    },

    totalItems(order) {
      return (order.carts || []).reduce((sum, cart) => sum + (cart.items || []).length, 0);
    },

    orderTotal(order) {
      return (order.carts || []).reduce((sum, cart) => {
        return sum + (cart.items || []).reduce((s, item) => {
          const qty   = item.pivot?.quantity ?? 1;
          const price = item.pivot?.price ?? item.price;
          return s + price * qty;
        }, 0);
      }, 0);
    },

    formatDate(dateStr) {
      if (!dateStr) return '—';
      return new Date(dateStr).toLocaleDateString('en-GB', {
        day: '2-digit', month: 'short', year: 'numeric',
      });
    },

    shortDate(dateStr) {
      if (!dateStr) return '';
      return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
    },

    statusClass(status) {
      return {
        'bg-emerald-100 text-emerald-700': status === 'PAID' || status === 'COMPLETED',
        'bg-orange-100 text-orange-600':   status === 'PENDING',
        'bg-red-100 text-red-600':         status === 'FAILED' || status === 'CANCELLED',
        'bg-blueGray-100 text-blueGray-500': !['PAID','COMPLETED','PENDING','FAILED','CANCELLED'].includes(status),
      };
    },

    loanPillClass(loan) {
      if (loan.status === 'PAID')    return 'bg-emerald-50 border-emerald-200 text-emerald-700';
      if (loan.status === 'DEFAULT') return 'bg-red-50 border-red-200 text-red-600';
      return 'bg-orange-50 border-orange-200 text-orange-600';
    },
  },
  mounted() {
    this.getEcomOrders();
  },
  created() {
    this.formatter = formatter;
  },
};
</script>
